// Registro central de imports lazy de páginas + prefetch selectivo.
// - App.tsx importa el mapa para lazy() y para precargar HOT_ROUTES tras idle.
// - DashboardLayout.tsx llama prefetchRoute() en onMouseEnter/onFocus para
//   descargar los chunks fríos justo cuando el usuario apunta al link.

export const pageImports = {
  Auth:            () => import("../pages/Auth"),
  Overview:        () => import("../pages/Overview"),
  Dashboard:       () => import("../pages/Dashboard"),
  Movements:       () => import("../pages/Movements"),
  Orders:          () => import("../pages/Orders"),
  UsersAndClients: () => import("../pages/UsersAndClients"),
  Invoices:        () => import("../pages/Invoices"),
  Profile:         () => import("../pages/Profile"),
  CheckInvoices:   () => import("../pages/CheckInvoices"),
  Settings:        () => import("../pages/Settings"),
  Administration:  () => import("../pages/Administration"),
  ClientsHub:      () => import("../pages/ClientsHub"),
  ClientDetail:    () => import("../pages/ClientDetail"),
  Amazon:          () => import("../pages/Amazon"),
  FacturaPro:      () => import("../pages/FacturaPro"),
  Trash:           () => import("../pages/Trash"),
  NotFound:        () => import("../pages/NotFound"),
} as const;

export type PageKey = keyof typeof pageImports;

/**
 * Rutas "calientes": las que el 90% de usuarios abre en cada sesión.
 * Se precargan tras `requestIdleCallback`. El resto (Administration, Trash,
 * FacturaPro con sus ~500 KB de PDF libs, etc.) esperan a on-hover.
 */
export const HOT_ROUTES: PageKey[] = ["Overview", "Amazon", "Movements", "Invoices"];

// Ruta → clave del mapa. Solo las accesibles vía sidebar.
const ROUTE_TO_KEY: Record<string, PageKey> = {
  "/dashboard":                  "Dashboard",
  "/dashboard/overview":         "Overview",
  "/dashboard/movements":        "Movements",
  "/dashboard/orders":           "Orders",
  "/dashboard/invoices":         "Invoices",
  "/dashboard/facturapro":       "FacturaPro",
  "/dashboard/check-invoices":   "CheckInvoices",
  "/dashboard/users":            "UsersAndClients",
  "/dashboard/clients":          "UsersAndClients",
  "/dashboard/amazon":           "Amazon",
  "/dashboard/settings":         "Settings",
  "/dashboard/administration":   "Administration",
  "/dashboard/administration/clients":  "ClientsHub",
  "/dashboard/profile":          "Profile",
};

// Evita disparar el mismo import() varias veces (cada llamada devuelve la
// misma promesa cacheada por webpack/vite, pero explicitar reduce ruido).
const prefetched = new Set<PageKey>();

export function prefetchRoute(url: string): void {
  const key = ROUTE_TO_KEY[url];
  if (!key || prefetched.has(key)) return;
  prefetched.add(key);
  pageImports[key]().catch(() => prefetched.delete(key));
}

/** Precarga las hot routes tras first paint. Llamar 1 vez desde App.tsx. */
export function prefetchHotRoutes(): void {
  if (typeof window === "undefined") return;
  const run = () => {
    for (const key of HOT_ROUTES) {
      if (prefetched.has(key)) continue;
      prefetched.add(key);
      pageImports[key]().catch(() => prefetched.delete(key));
    }
  };
  if ("requestIdleCallback" in window) {
    (window as any).requestIdleCallback(run);
  } else {
    setTimeout(run, 2000);
  }
}
